import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faTicket, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { animated, config, useSpring } from '@react-spring/web';
import { withScope } from '@sentry/core';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled, { keyframes } from 'styled-components';

import {
  InGameCurrency,
  Rarity,
  WheelType,
} from '@sorare/core/src/__generated__/globalTypes';
import { Impact } from '@sorare/core/src/atoms/animations/Impact';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_HOME } from '@sorare/core/src/constants/__generated__/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useShortcut } from '@sorare/core/src/hooks/useShortcut';
import { shuffle } from '@sorare/core/src/lib/arrays';
import { sendSafeError } from '@sorare/core/src/lib/error';
import { glossary, wheel } from '@sorare/core/src/lib/glossary';
import { withFragments } from '@sorare/core/src/lib/gql';
import { getSafePreviousLocation } from '@sorare/core/src/lib/navigation/getSafePreviousLocation';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Background } from './Background';
import { BoostedOdds } from './BoostedOdds';
import { GetMoreTickets } from './GetMoreTickets';
import { GoldenTickets } from './GoldenTickets';
import { Odds } from './Odds';
import { Reward } from './Reward';
import { Slot } from './Slot';
import { Spinner } from './Spinner';
import { Tickets } from './Tickets';
import { WheelRaritySwitcher } from './WheelRaritySwitcher';
import type {
  SpinWheelMutation,
  SpinWheelMutationVariables,
  WheelQuery,
  WheelQueryVariables,
  isWorthyOfCelebration_ProbabilisticBundleSlotConfig,
} from './__generated__/index.graphql';

const openingAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2%) scale(0.95);
  }
`;

const Root = styled.div`
  animation: 0.2s ease-out ${openingAnimation} forwards;
`;

const Wrapper = styled(Vertical)`
  position: relative;
  isolation: isolate;
  width: 100%;
  align-items: stretch;
  min-height: var(--100vh);
  @media ${tabletAndAbove} {
    overflow: hidden;
  }

  & > :first-child {
    position: absolute;
    inset: 0;
    z-index: -1;
  }
`;

const Grid = styled.div`
  flex-grow: 1;
  width: 100%;
  display: grid;
  overflow: hidden;
  align-content: stretch;
  align-items: center;
  justify-items: center;
  grid-template-areas:
    'header header header'
    'wheel wheel wheel';
  grid-template-rows: min-content 1fr;

  @media ${tabletAndAbove} {
    min-height: var(--100vh);
    grid-template-areas:
      'header header header'
      'wheel wheel wheel'
      'buttons buttons buttons';
    grid-template-rows: 1fr min-content 1fr;
  }
`;

const Header = styled(animated.div)<{ $withGetMoreTickets?: boolean }>`
  grid-area: header;
  display: grid;
  width: 100%;
  height: 100%;
  align-content: stretch;
  align-items: stretch;
  ${({ $withGetMoreTickets }) =>
    $withGetMoreTickets
      ? `grid-template-columns: 1fr 1fr;
      grid-template-areas:
    'title title'
    'getMoreTickets tickets'
    'golden golden'
  `
      : `
      grid-template-columns: 1fr;
      grid-template-areas:
    'title'
    'tickets'
    'golden'
  `};
  grid-template-rows: min-content min-content min-content;

  > * {
    padding: var(--intermediate-unit);
    border-color: var(--c-nd-200);
    border-width: 0 0 1px;
    border-style: solid;

    &:nth-child(3) {
      border-width: 0 0 1px 1px;
    }
    &:not(:first-child) {
      backdrop-filter: blur(10px);
    }
  }
  @media ${tabletAndAbove} {
    ${({ $withGetMoreTickets }) =>
      $withGetMoreTickets
        ? `
        grid-template-columns: 1fr 1fr 1fr;
        grid-template-areas:
        'title title title'
        'getMoreTickets tickets golden';`
        : `
        grid-template-columns: 1fr 1fr;
        grid-template-areas:
        'title title'
        'tickets golden';`};
    > * {
      border-width: 0 0 1px;
      &:nth-child(3) {
        border-width: 0 1px 1px;
      }
    }
  }
`;

const Footer = styled(animated(Horizontal)).attrs({
  center: true,
})``;

const FooterRoot = styled(Vertical)`
  grid-area: buttons;
  position: sticky;
  bottom: 0;
  z-index: 1;
  overflow: hidden;
  padding: var(--double-unit);
  &.nonInteractive {
    pointer-events: none;
  }
`;

const StyledDrawer = styled(Drawer)`
  width: 100%;
  overflow-y: auto;
  @media ${tabletAndAbove} {
    width: 500px;
  }
`;

const SwitcherWrapper = styled(animated.div)`
  position: absolute;
  bottom: 0;
  left: 20%;
`;

const LogoTitle = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const isWorthyOfCelebration = withFragments(
  (probableConfig?: isWorthyOfCelebration_ProbabilisticBundleSlotConfig) => {
    if (!probableConfig) {
      return false;
    }
    if (
      probableConfig.deliverableItems?.length ||
      probableConfig.cards?.length ||
      probableConfig.cardShards?.length ||
      probableConfig.conversionCredit
    ) {
      return true;
    }

    // not just tickets or golden tickets
    return probableConfig.inGameCurrencies?.some(
      x =>
        ![
          InGameCurrency.GOLDEN_WHEEL_TICKET,
          InGameCurrency.WHEEL_TICKET,
        ].includesValue(x.currency)
    );
  },
  {
    ProbabilisticBundleSlotConfig: gql`
      fragment isWorthyOfCelebration_ProbabilisticBundleSlotConfig on ProbabilisticBundleSlotConfig {
        id
        deliverableItems {
          quantity
        }
        cards {
          quality
        }
        cardShards {
          id
        }
        inGameCurrencies {
          id
          currency
        }
        conversionCredit {
          id
        }
      }
    ` as TypedDocumentNode<isWorthyOfCelebration_ProbabilisticBundleSlotConfig>,
  }
);

const query = gql`
  query WheelQuery($sport: Sport!, $rarity: Rarity!) {
    config {
      id
    }
    market {
      id
      tasks(sport: $sport, genre: DAILY_WHEEL_TICKET) {
        id
        ...GetMoreTickets_taskInterface
      }
      wheelCosts(sport: $sport, rarity: $rarity) {
        currency
        quantity
        wheelType
      }
      wheelOdds(sport: $sport, wheelType: STANDARD, rarity: $rarity) {
        id
        slots {
          probableConfigs {
            id
            ...Slot_probabilisticBundleSlotConfig
            ...Reward_probabilisticBundleSlotConfig
            ...isWorthyOfCelebration_ProbabilisticBundleSlotConfig
          }
        }
      }
      goldenWheelOdds: wheelOdds(
        sport: $sport
        wheelType: GOLDEN
        rarity: $rarity
      ) {
        id
        slots {
          probableConfigs {
            id
            ...Slot_probabilisticBundleSlotConfig
            ...Reward_probabilisticBundleSlotConfig
            ...isWorthyOfCelebration_ProbabilisticBundleSlotConfig
          }
        }
      }
      activeWheelConfig(sport: $sport, rarity: $rarity) {
        slug
        title
        logoUrl
        dailyClaimTask
        goldenWheelCost: wheelCost(wheelType: GOLDEN) {
          quantity
        }
        wheelOdds(wheelType: STANDARD) {
          id
          slots {
            probableConfigs {
              id
              ...Slot_probabilisticBundleSlotConfig
              ...Reward_probabilisticBundleSlotConfig
              ...isWorthyOfCelebration_ProbabilisticBundleSlotConfig
            }
          }
        }
        goldenWheelOdds: wheelOdds(wheelType: GOLDEN) {
          id
          slots {
            probableConfigs {
              id
              ...Slot_probabilisticBundleSlotConfig
              ...Reward_probabilisticBundleSlotConfig
              ...isWorthyOfCelebration_ProbabilisticBundleSlotConfig
            }
          }
        }
      }
    }
  }
  ${Slot.fragments.ProbabilisticBundleSlotConfig}
  ${Reward.fragments.probabilisticBundleSlotConfig}
  ${GetMoreTickets.fragments.taskInterface}
  ${isWorthyOfCelebration.fragments.ProbabilisticBundleSlotConfig}
` as TypedDocumentNode<WheelQuery, WheelQueryVariables>;

const spinWheelMutation = gql`
  mutation SpinWheelMutation(
    $sport: Sport!
    $wheelType: WheelType!
    $rarity: Rarity!
  ) {
    spinWheel(
      input: { sport: $sport, wheelType: $wheelType, rarity: $rarity }
    ) {
      errors {
        code
        message
        path
      }
      reward {
        id
        drawnSlotConfigIds
        items {
          id
          ...Reward_probabilisticBundleSlotItemInterface
        }
      }
      currentUser {
        slug
        wheelTickets: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [WHEEL_TICKET, COMMON_WHEEL_TICKET]
        ) {
          amount
          currency
        }
        goldenWheelTickets: inGameCurrencyBalances(
          sport: $sport

          inGameCurrencies: [GOLDEN_WHEEL_TICKET, COMMON_GOLDEN_WHEEL_TICKET]
        ) {
          amount
          currency
        }
      }
    }
  }
  ${Reward.fragments.probabilisticBundleSlotItemInterface}
` as TypedDocumentNode<SpinWheelMutation, SpinWheelMutationVariables>;

type Props = {
  rarity: Rarity;
};

const WheelComponent = ({ rarity }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const navigate = useNavigate();
  const { isIosApp, postMessage } = useIsMobileApp();
  const onClose = () => {
    if (isIosApp) {
      postMessage('closeCurrentWebview');
    }
    navigate(getSafePreviousLocation(generateSportContextPath(ANY_SPORT_HOME)));
  };
  useShortcut('Escape', onClose);
  const {
    flags: { useDailyFreeWheelTicket = false, useAdminWheelConfig = false },
  } = useFeatureFlags();

  const isTabletAndAbove = useIsTabletAndAbove();
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();

  const { currentUser } = useCurrentUserContext();
  const nbTickets =
    currentUser?.wheelTickets.find(x =>
      rarity === Rarity.common
        ? x.currency === InGameCurrency.COMMON_WHEEL_TICKET
        : x.currency === InGameCurrency.WHEEL_TICKET
    )?.amount ?? 0;
  const currentNbGoldenTickets =
    currentUser?.goldenWheelTickets.find(x =>
      rarity === Rarity.common
        ? x.currency === InGameCurrency.COMMON_GOLDEN_WHEEL_TICKET
        : x.currency === InGameCurrency.GOLDEN_WHEEL_TICKET
    )?.amount ?? 0;
  const [nbGoldenTickets, setNbGoldenTickets] = useState(
    currentNbGoldenTickets
  );
  const [wheelType, setWheelType] = useState<WheelType | undefined>(undefined);
  const [hasOpened, setHasOpened] = useState(false);
  const close = useCallback(() => setWheelType(undefined), [setWheelType]);
  const { sport } = useSportContext();
  const { data } = useQuery(query, {
    variables: {
      sport,
      rarity,
    },
  });

  const useDailyTask = useAdminWheelConfig
    ? data?.market?.activeWheelConfig?.dailyClaimTask
    : useDailyFreeWheelTicket && rarity !== Rarity.common;

  const dailyWheelTicketTask = useDailyTask
    ? data?.market?.tasks[0]
    : undefined;

  const oldWayGoldenWheelCost =
    (data?.market?.wheelCosts ?? []).find(x => x.wheelType === WheelType.GOLDEN)
      ?.quantity ?? 10;

  const newGoldenWheelCost =
    data?.market?.activeWheelConfig?.goldenWheelCost?.quantity ?? 10;

  const goldenWheelCost = useAdminWheelConfig
    ? newGoldenWheelCost
    : oldWayGoldenWheelCost;

  const wheelOdds = useAdminWheelConfig
    ? data?.market?.activeWheelConfig?.wheelOdds
    : data?.market?.wheelOdds;
  const goldenWheelOdds = useAdminWheelConfig
    ? data?.market?.activeWheelConfig?.goldenWheelOdds
    : data?.market?.goldenWheelOdds;

  const [spinWheel] = useMutation(spinWheelMutation);
  const [spinning, setSpinning] = useState(false);

  const [isRewardShown, setIsRewardShown] = useState(false);
  const [showUI, setShowUI] = useState(true);

  const { updateDOM } = useTransitionApi();
  const toggleReward = useCallback(
    (value: boolean) => {
      updateDOM(() => {
        setIsRewardShown(value);
      });
    },
    [updateDOM]
  );

  const [currentState, setCurrentState] = useState<{
    item?: NonNullable<
      NonNullable<SpinWheelMutation['spinWheel']>['reward']
    >['items'][number];
    index?: number;
    isGoldenWheel: boolean;
    probableConfigs: NonNullable<
      typeof wheelOdds
    >['slots'][number]['probableConfigs'];
  }>({
    item: undefined,
    index: undefined,
    isGoldenWheel: false,
    probableConfigs: [],
  });

  const resetState = useCallback(() => {
    const isGoldenWheel = currentNbGoldenTickets >= goldenWheelCost;
    const currentOdds = isGoldenWheel ? goldenWheelOdds : wheelOdds;

    if (currentOdds?.slots?.[0]?.probableConfigs) {
      if (
        currentState.probableConfigs.length === 0 ||
        isGoldenWheel !== currentState.isGoldenWheel
      ) {
        setCurrentState({
          probableConfigs: currentOdds.slots[0].probableConfigs,
          isGoldenWheel,
          item: undefined,
          index: undefined,
        });
      } else {
        setCurrentState(c => ({
          ...c,
          item: undefined,
          index: undefined,
        }));
      }
    }
  }, [
    currentNbGoldenTickets,
    goldenWheelCost,
    goldenWheelOdds,
    wheelOdds,
    currentState.probableConfigs.length,
    currentState.isGoldenWheel,
  ]);

  if (currentState.probableConfigs.length === 0) {
    resetState();
  }

  const canSpin = nbTickets > 0 || currentState.isGoldenWheel;

  const uiSpring = useSpring({
    up: showUI ? 0 : -100,
    down: showUI ? 0 : 100,
    opacity: showUI ? 1 : 0,
    config: config.gentle,
  });

  const spin = async () => {
    // Guard against empty configs - this can happen if data hasn't loaded
    // or if there's a race condition with isGoldenWheel state changes
    if (currentState?.probableConfigs.length === 0) {
      withScope(scope => {
        scope.setExtra('wheelOddsSlots', wheelOdds?.slots);
        scope.setExtra('goldenWheelOddsSlots', goldenWheelOdds?.slots);
        scope.setExtra('isGoldenWheel', currentState.isGoldenWheel);
        sendSafeError(
          new Error('Cannot spin wheel: no probable configs available')
        );
      });
      return -1;
    }
    setSpinning(true);
    toggleReward(false);
    setShowUI(false);
    const shuffledProbableConfigs = shuffle(currentState.probableConfigs);
    setCurrentState(c => ({
      ...c,
      probableConfigs: shuffledProbableConfigs,
      item: undefined,
      index: undefined,
    }));
    const [{ data: spinWheelData }] = await Promise.all([
      spinWheel({
        variables: {
          sport,
          wheelType: currentState.isGoldenWheel
            ? WheelType.GOLDEN
            : WheelType.STANDARD,
          rarity,
        },
      }),
      // minimum delay
      new Promise(resolve => setTimeout(resolve, 1000)),
    ]);

    const hasErrors = (spinWheelData?.spinWheel?.errors.length ?? 0) > 0;

    if (!hasErrors && !spinWheelData?.spinWheel?.reward?.items?.length) {
      throw new Error('Error spinning wheel');
    }

    // currentState.probableConfigs has a stale value here, we need to use shuffledProbableConfigs
    const index = shuffledProbableConfigs.findIndex(x =>
      spinWheelData?.spinWheel?.reward?.drawnSlotConfigIds.includes(x.id)
    );

    if (!hasErrors && index === -1) {
      withScope(scope => {
        scope.setExtra(
          'drawnSlotConfigIds',
          spinWheelData?.spinWheel?.reward?.drawnSlotConfigIds
        );
        scope.setExtra('shuffledProbableConfigs', shuffledProbableConfigs);
        scope.setExtra('wheelOddsSlots', wheelOdds?.slots);
        scope.setExtra('goldenWheelOddsSlots', goldenWheelOdds?.slots);
        scope.setExtra('isGoldenWheel', currentState.isGoldenWheel);
        sendSafeError(new Error('Cannot match slot to reward'));
      });
    }

    setCurrentState(c => ({
      ...c,
      item: spinWheelData?.spinWheel?.reward?.items[0],
      index,
    }));

    return index;
  };

  const endSpinUX = () => {
    toggleReward(false);
    setNbGoldenTickets(currentNbGoldenTickets);
    resetState();
    setShowUI(true);
  };

  const footer = (
    <FooterRoot
      center
      className={classNames({
        nonInteractive: !showUI,
      })}
    >
      <Footer style={{ y: uiSpring.down, opacity: uiSpring.opacity }}>
        <Button
          color="tertiary"
          size="large"
          onClick={() => {
            setWheelType(
              currentState.isGoldenWheel ? WheelType.GOLDEN : WheelType.STANDARD
            );
            setHasOpened(true);
          }}
        >
          <FormattedMessage {...glossary.rewards} />
        </Button>
        <LoadingButton
          size="large"
          color="primary"
          onClick={() => {
            spin().catch(error => {
              sendSafeError(error);
            });
          }}
          loading={spinning}
          disabled={spinning || !canSpin}
        >
          <Impact trigger={currentState.isGoldenWheel}>
            {currentState.isGoldenWheel ? (
              <FormattedMessage
                id="marketplace.wheel.spinGolden"
                defaultMessage="Free Spin"
              />
            ) : (
              <>
                <FormattedMessage {...wheel.spin} />{' '}
                <span style={{ opacity: 0.5 }}>·</span> 1{' '}
                <FontAwesomeIcon icon={faTimes} size="sm" />{' '}
                <FontAwesomeIcon icon={faTicket} />
              </>
            )}
          </Impact>
        </LoadingButton>
      </Footer>
      {!isTabletAndAbove && (
        <animated.div style={{ y: uiSpring.down, opacity: uiSpring.opacity }}>
          <WheelRaritySwitcher rarity={rarity} />
        </animated.div>
      )}
    </FooterRoot>
  );

  const withGetMoreTickets =
    !!dailyWheelTicketTask ||
    (rarity === Rarity.common && !!currentUser?.showMissionPage);
  return (
    <Wrapper>
      <Background
        golden={currentState.isGoldenWheel}
        slug={data?.market?.activeWheelConfig?.slug}
      />
      <Grid>
        <Header
          style={{ y: uiSpring.up, opacity: uiSpring.opacity }}
          $withGetMoreTickets={withGetMoreTickets}
        >
          <Centered
            style={{
              gridArea: 'title',
              justifySelf: 'stretch',
              padding: 'var(--double-unit)',
            }}
            right={<CloseButton onClose={onClose} size="small" />}
          >
            {useAdminWheelConfig ? (
              <>
                {data?.market?.activeWheelConfig?.logoUrl ? (
                  <LogoTitle
                    src={data?.market?.activeWheelConfig?.logoUrl}
                    alt={data?.market?.activeWheelConfig?.title}
                  />
                ) : (
                  <BrandL>{data?.market?.activeWheelConfig?.title}</BrandL>
                )}
              </>
            ) : (
              <BrandL>
                <FormattedMessage
                  id="marketplace.wheel.title"
                  defaultMessage="Sorare Wheel"
                />
              </BrandL>
            )}
          </Centered>
          {!!withGetMoreTickets && (
            <GetMoreTickets
              dailyWheelTicketTask={dailyWheelTicketTask}
              dailyTasksExpiresAt={dailyTasksExpiresAt ?? undefined}
              rarity={rarity}
            />
          )}
          <Tickets nb={nbTickets} rarity={rarity} />
          <GoldenTickets
            nb={nbGoldenTickets}
            cost={goldenWheelCost}
            onInfoCircle={() => {
              setWheelType(WheelType.GOLDEN);
              setHasOpened(true);
            }}
          />
        </Header>

        <div
          style={{
            gridArea: 'wheel',
            alignSelf: 'stretch',
            justifySelf: 'stretch',
          }}
        >
          <BoostedOdds show={currentState.isGoldenWheel} />
          <Spinner
            spin={spinning}
            stopAtIndex={currentState.index}
            onSpinEnd={() => {
              setSpinning(false);
              // is the reward worthy of celebration?
              if (
                currentState.index !== undefined &&
                isWorthyOfCelebration(
                  currentState.probableConfigs[currentState.index]
                )
              ) {
                toggleReward(true);
              } else {
                endSpinUX();
              }
            }}
          >
            {currentState.probableConfigs.map((probableConfig, index) => (
              <Slot
                big
                key={probableConfig.id}
                config={probableConfig}
                style={
                  index === currentState.index && !isRewardShown
                    ? { viewTransitionName: 'wheel-reward' }
                    : {}
                }
                rarity={rarity}
              />
            ))}
          </Spinner>
        </div>

        {!!isRewardShown &&
          !!currentState?.item &&
          currentState?.index !== undefined && (
            <Reward
              rarity={rarity}
              onClose={endSpinUX}
              item={currentState.item}
              config={currentState.probableConfigs[currentState.index]}
            />
          )}

        <StyledDrawer
          open={!!wheelType}
          onBackdropClick={close}
          onClose={close}
          keepMounted={hasOpened}
        >
          <Odds
            sport={sport}
            onClose={close}
            onWheelTypeChange={setWheelType}
            wheelType={wheelType}
            rarity={rarity}
          />
        </StyledDrawer>
        {!!isTabletAndAbove && footer}
      </Grid>
      {!isTabletAndAbove && footer}
      {!!isTabletAndAbove && (
        <SwitcherWrapper
          style={{ y: uiSpring.down, opacity: uiSpring.opacity }}
        >
          <WheelRaritySwitcher rarity={rarity} />
        </SwitcherWrapper>
      )}
    </Wrapper>
  );
};

export const Wheel = ({ rarity }: Props) => {
  return (
    <Root>
      <WheelComponent
        rarity={rarity}
        // reset wheelState when rarity changes
        key={rarity}
      />
    </Root>
  );
};
